import CheckCircleIcon from '@src/components/icons/CheckCircle'
import CloseIcon from '@src/components/icons/Close'
import { ITransactionStatus } from '@src/types/transactionStatus'
import { formatExchangeAmountWithCommas } from '@src/utils/exchangeUtils'
import { createContextLogger } from '@src/utils/logger'
import { useConfigStore } from '@src/zustand_stores'
import { useApplicationStore } from '@src/zustand_stores/application.store'

const statusCardLogger = createContextLogger('TransactionStatusCard')

interface TransactionStatusCardProps {
    exchange: ITransactionStatus
    onClose?: () => void // Callback for when the card is closed
    isSuccess?: boolean
    isModalCloseNotification?: boolean // Indicates if this is being used as a modal close notification
}

const TransactionStatusCard = (props: TransactionStatusCardProps) => {
    const { exchange, isSuccess = false, isModalCloseNotification = false, onClose } = props
    const setCurrentModalId = useApplicationStore(state => state.setCurrentModalId)
    const availableNetworks = useConfigStore(config => config.availableNetworks)
    const { sellData, buyData } = exchange

    // Log transaction data to help debug token display issues
    statusCardLogger.debug('Rendering TransactionStatusCard with data:', {
        sellToken: sellData?.token?.symbol,
        sellAmount: sellData?.value?.toString(),
        sellChain: sellData?.chainId,
        buyToken: buyData?.token?.symbol,
        buyAmount: buyData?.value?.toString(),
        buyChain: buyData?.chainId,
        isSuccess,
        exchangeId: exchange.exchangeId,
    })

    // Safe wrapper for onClose to prevent errors
    const handleClose = (e?: React.MouseEvent) => {
        // Stop propagation if event is provided
        if (e) {
            e.preventDefault()
            e.stopPropagation()
        }

        try {
            // Call onClose if provided
            if (typeof onClose === 'function') {
                onClose()
            }
        } catch (error) {
            console.error('Error closing notification:', error)
        }
    }

    // Safe handler for card click
    const handleCardClick = (e: React.MouseEvent) => {
        e.preventDefault()
        try {
            setCurrentModalId(exchange.exchangeId)
            handleClose()
        } catch (error) {
            console.error('Error handling card click:', error)
        }
    }

    return (
        <div
            className={`rounded-md border ${isSuccess ? 'border-green-500/30' : 'border-modal-border/15'} ${isSuccess ? 'bg-green-50/10' : 'bg-modal-background'}`}
            onClick={handleCardClick}
        >
            <div
                className={`mt-2 flex items-center justify-between border-b ${isSuccess ? 'border-green-500/20' : 'border-modal-border/10'} px-4 py-2`}
            >
                <div className='flex items-center'>
                    {isSuccess && <CheckCircleIcon className='mr-2 h-5 w-5 text-green-500' />}
                    <span
                        className={`${isSuccess ? 'text-base' : 'text-lg'} font-semibold ${isSuccess ? 'text-green-600' : ''}`}
                    >
                        {isSuccess
                            ? 'Transaction Successful'
                            : isModalCloseNotification
                              ? 'Transaction In Progress'
                              : 'Transaction Update'}
                    </span>
                </div>
                <CloseIcon className='cursor-pointer hover:opacity-70' onClick={handleClose} />
            </div>
            <div className='flex w-full flex-col py-3'>
                <div className={`mx-auto ${isSuccess ? 'mb-4' : 'mb-6'} flex items-center`}>
                    <div className='relative mr-[12px]'>
                        <img
                            src={sellData?.token?.logoURI || '/assets/token/unknown.png'}
                            onError={e => {
                                e.currentTarget.src = '/assets/token/unknown.png'
                            }}
                            className='relative w-8 rounded-full shadow-[3px_3px_5px_0px_rgba(0,0,0,.55)] md:w-10'
                            alt={sellData?.token?.symbol || 'Unknown token'}
                        />
                        <img
                            src={availableNetworks[sellData?.chainId]?.icon || '/assets/network/ethereum.png'}
                            className='absolute -right-[12px] bottom-0 w-5 rounded-full shadow-[4px_2px_2px_0_rgba(0,0,0,.2)] md:w-6'
                            alt={availableNetworks[sellData?.chainId]?.name || 'Unknown network'}
                        />
                        <span className='absolute left-[35px] max-w-[80px] truncate whitespace-nowrap font-bold italic md:text-lg'>
                            {/* Safely handle BigInt values */}
                            {typeof sellData?.value === 'undefined' ||
                            (typeof sellData?.value === 'bigint' && sellData.value.toString() === '0')
                                ? '0' // Fallback for BigInt zeros or undefined values
                                : formatExchangeAmountWithCommas(sellData)}
                        </span>
                    </div>

                    <img src='/images/arrow-right.png' alt='arrow' className='mx-6 w-12 animate-pulse' />

                    <div className='relative mr-[12px]'>
                        <img
                            src={buyData?.token?.logoURI || '/assets/token/unknown.png'}
                            onError={e => {
                                e.currentTarget.src = '/assets/token/unknown.png'
                            }}
                            className='relative w-8 rounded-full shadow-[3px_3px_5px_0px_rgba(0,0,0,.55)] md:w-10'
                            alt={buyData?.token?.symbol || 'Unknown token'}
                        />
                        <img
                            src={availableNetworks[buyData?.chainId]?.icon || '/assets/network/ethereum.png'}
                            className='absolute -right-[12px] bottom-0 w-5 rounded-full shadow-[4px_2px_2px_0_rgba(0,0,0,.2)] md:w-6'
                            alt={availableNetworks[buyData?.chainId]?.name || 'Unknown network'}
                        />
                        <span className='absolute left-[35px] max-w-[80px] truncate whitespace-nowrap font-bold italic md:text-lg'>
                            {/* Safely handle BigInt values */}
                            {typeof buyData?.value === 'undefined' ||
                            (typeof buyData?.value === 'bigint' && buyData.value.toString() === '0')
                                ? '0' // Fallback for BigInt zeros or undefined values
                                : formatExchangeAmountWithCommas(buyData)}
                        </span>
                    </div>
                </div>
                <span className={`mt-1 px-4 py-2 text-sm ${isSuccess ? 'text-green-600' : 'text-gray-600'}`}>
                    {isSuccess
                        ? 'Your swap has been completed successfully!'
                        : isModalCloseNotification
                          ? "This notification will disappear, but we'll continue processing your swap!"
                          : 'This status card will disappear in a moment but we will continue processing your swap!'}
                </span>
            </div>
        </div>
    )
}

export default TransactionStatusCard
